// Canonical map of the trial-prep workflow, used by the right-hand workflow
// rail (components/layout/WorkflowRail.tsx) to render "Recommended Next Steps"
// and "Quick Links".
//
// Two rules keep this honest, both enforced by src/test/content/workflowMap.test.ts:
//   1. Every key of ROUTE_META must be a real route declared in App.tsx.
//   2. Every path referenced in a `quickLinks` array or a `next.to` must itself
//      be a key of ROUTE_META, so no link in the rail can dangle.
//
// Copy follows the product language policy in CLAUDE.md: jury vocabulary, and
// no em-dashes in anything a user reads.

export type StageId =
  | 'overview'
  | 'intake'
  | 'discovery'
  | 'jury'
  | 'arguments'
  | 'simulate'
  | 'analyze'
  | 'resolution'
  | 'practice';

export interface StageDef {
  id: StageId;
  label: string;
  /** One line describing what this part of the workflow is for. */
  blurb: string;
}

/** Ordered as a lawyer actually moves through a matter. */
export const WORKFLOW_STAGES: StageDef[] = [
  { id: 'overview', label: 'Overview', blurb: 'Where your portfolio stands today.' },
  { id: 'intake', label: 'Case Setup', blurb: 'Open the matter and capture the theory.' },
  { id: 'discovery', label: 'Discovery and Evidence', blurb: 'Gather the record you will argue from.' },
  { id: 'jury', label: 'Jury', blurb: 'Build the jury you want to test against.' },
  { id: 'arguments', label: 'Arguments', blurb: 'Draft and sharpen what you will say.' },
  { id: 'simulate', label: 'Simulation', blurb: 'Argue it before it counts.' },
  { id: 'analyze', label: 'Analysis', blurb: 'Read the verdict and find the weak seams.' },
  { id: 'resolution', label: 'Resolution', blurb: 'Settle, mediate, plead, or appeal.' },
  { id: 'practice', label: 'Practice', blurb: 'Firm, billing, and account management.' },
];

export const STAGE_BY_ID: Record<StageId, StageDef> = WORKFLOW_STAGES.reduce(
  (acc, stage) => {
    acc[stage.id] = stage;
    return acc;
  },
  {} as Record<StageId, StageDef>
);

export interface ContextualNext {
  /** Button copy. */
  label: string;
  /** A path that is also a key of ROUTE_META. */
  to: string;
  /** Why this is the natural move from the current page. */
  why: string;
}

export interface RouteMeta {
  /** Human label, reused everywhere this path is linked. */
  label: string;
  stage: StageId;
  /** Sibling and downstream pages worth one click from here. */
  quickLinks: string[];
  /** The single obvious follow-on action from this page. */
  next?: ContextualNext;
}

/**
 * Paths that read `?case=<id>` on mount, either through useSelectedCase or
 * their own query parsing. Links to these carry the active case forward so the
 * rail never drops the user's context.
 */
export const CASE_AWARE_PATHS = new Set<string>([
  '/compare',
  '/experts',
  '/feedback',
  '/generator',
  '/mock-panel',
  '/panel-instructions',
  '/panel-pool',
  '/panel-questions',
  '/panel-selection',
  '/personas',
  '/scenarios',
  '/simulations/new',
  '/versions',
  '/weakness',
]);

export const ROUTE_META: Record<string, RouteMeta> = {
  // ─── Overview ──────────────────────────────────────────────────────────────
  '/dashboard': {
    label: 'Dashboard',
    stage: 'overview',
    quickLinks: ['/simulations/new', '/history', '/analytics', '/calendar'],
    next: {
      label: 'Open your cases',
      to: '/cases',
      why: 'Every simulation hangs off a case.',
    },
  },
  '/analytics': {
    label: 'Analytics',
    stage: 'analyze',
    quickLinks: ['/history', '/compare', '/feedback', '/weakness'],
    next: {
      label: 'Compare simulations',
      to: '/compare',
      why: 'Put two runs side by side to see what moved the numbers.',
    },
  },
  '/ai-tools': {
    label: 'AI Tools',
    stage: 'practice',
    quickLinks: ['/generator', '/opening', '/closing', '/cross-exam', '/weakness', '/settlement'],
    next: {
      label: 'Generate an argument',
      to: '/generator',
      why: 'The generator is the fastest way into a draft.',
    },
  },

  // ─── Case setup ────────────────────────────────────────────────────────────
  '/intake': {
    label: 'Client Intake',
    stage: 'intake',
    quickLinks: ['/notes', '/timeline', '/communications', '/calendar'],
    next: {
      label: 'Create the case',
      to: '/cases',
      why: 'Turn the intake into a case you can run simulations against.',
    },
  },
  '/cases': {
    label: 'Cases',
    stage: 'intake',
    quickLinks: ['/intake', '/mock-panel', '/simulations/new', '/timeline', '/tasks', '/collaboration'],
    next: {
      label: 'Create a jury',
      to: '/mock-panel',
      why: 'A case needs a jury before it can be simulated.',
    },
  },
  '/cases/:caseId': {
    label: 'Case Detail',
    stage: 'intake',
    quickLinks: ['/mock-panel', '/simulations/new', '/evidence/upload', '/notes', '/timeline'],
    next: {
      label: 'Create a jury for this case',
      to: '/mock-panel',
      why: 'Generate the jury you want to argue in front of.',
    },
  },
  '/notes': {
    label: 'Case Notes',
    stage: 'intake',
    quickLinks: ['/timeline', '/tasks', '/strategy', '/collaboration'],
    next: {
      label: 'Build the case timeline',
      to: '/timeline',
      why: 'Notes turn into a chronology you can argue from.',
    },
  },
  '/timeline': {
    label: 'Case Timeline',
    stage: 'intake',
    quickLinks: ['/notes', '/discovery', '/checklist', '/calendar'],
    next: {
      label: 'Track discovery',
      to: '/discovery',
      why: 'A dated chronology tells you what discovery is still missing.',
    },
  },
  '/collaboration': {
    label: 'Case Collaboration',
    stage: 'intake',
    quickLinks: ['/notes', '/tasks', '/firm/members', '/communications'],
    next: {
      label: 'Assign tasks',
      to: '/tasks',
      why: 'Turn the discussion into owned work.',
    },
  },
  '/communications': {
    label: 'Client Communications',
    stage: 'intake',
    quickLinks: ['/intake', '/notes', '/calendar', '/invoices'],
    next: {
      label: 'Check the calendar',
      to: '/calendar',
      why: 'Client updates usually follow a date on the calendar.',
    },
  },

  // ─── Discovery and evidence ────────────────────────────────────────────────
  '/discovery': {
    label: 'Discovery Tracker',
    stage: 'discovery',
    quickLinks: ['/depositions', '/documents', '/evidence/upload', '/witnesses', '/timeline', '/tasks'],
    next: {
      label: 'Upload evidence',
      to: '/evidence/upload',
      why: 'Documents you have collected can be analyzed right away.',
    },
  },
  '/depositions': {
    label: 'Deposition Prep',
    stage: 'discovery',
    quickLinks: ['/witnesses', '/experts', '/cross-exam', '/discovery', '/documents'],
    next: {
      label: 'Build cross-examination',
      to: '/cross-exam',
      why: 'Deposition testimony is the raw material for cross.',
    },
  },
  '/witnesses': {
    label: 'Witnesses',
    stage: 'discovery',
    quickLinks: ['/experts', '/depositions', '/cross-exam', '/exhibits', '/discovery'],
    next: {
      label: 'Prepare cross-examination',
      to: '/cross-exam',
      why: 'Every witness on the list needs a cross outline.',
    },
  },
  '/experts': {
    label: 'Expert Witnesses',
    stage: 'discovery',
    quickLinks: ['/witnesses', '/depositions', '/cross-exam', '/exhibits', '/research'],
    next: {
      label: 'Test the expert framing',
      to: '/simulations/new',
      why: 'Expert testimony lands differently with different juries.',
    },
  },
  '/evidence/upload': {
    label: 'Evidence Upload',
    stage: 'discovery',
    quickLinks: ['/evidence/analyzer', '/documents', '/exhibits', '/discovery'],
    next: {
      label: 'Analyze what you uploaded',
      to: '/evidence/analyzer',
      why: 'Extracted text is only useful once it is scored.',
    },
  },
  '/evidence/analyzer': {
    label: 'Evidence Analyzer',
    stage: 'discovery',
    quickLinks: ['/evidence/upload', '/exhibits', '/documents', '/weakness', '/simulations/new'],
    next: {
      label: 'Predict exhibit impact',
      to: '/exhibits',
      why: 'See which of these documents will actually move a jury.',
    },
  },
  '/exhibits': {
    label: 'Exhibit Impact',
    stage: 'discovery',
    quickLinks: ['/evidence/analyzer', '/evidence/upload', '/witnesses', '/presentation', '/simulations/new'],
    next: {
      label: 'Run a simulation',
      to: '/simulations/new',
      why: 'Exhibit scores are estimates until a jury reacts to them.',
    },
  },
  '/documents': {
    label: 'Document Templates',
    stage: 'discovery',
    quickLinks: ['/evidence/upload', '/discovery', '/settlement-demand', '/research', '/rules'],
    next: {
      label: 'Draft a settlement demand',
      to: '/settlement-demand',
      why: 'The demand letter is the template most matters need first.',
    },
  },
  '/research': {
    label: 'Brief Research Library',
    stage: 'discovery',
    quickLinks: ['/rules', '/documents', '/strategy', '/arguments', '/appeals'],
    next: {
      label: 'Fold it into strategy',
      to: '/strategy',
      why: 'Research is only worth the time if it changes the plan.',
    },
  },
  '/rules': {
    label: 'Court Rules',
    stage: 'discovery',
    quickLinks: ['/research', '/calendar', '/checklist', '/documents'],
    next: {
      label: 'Check the trial checklist',
      to: '/checklist',
      why: 'Local rules drive most of what the checklist tracks.',
    },
  },

  // ─── Jury ──────────────────────────────────────────────────────────────────
  '/mock-panel': {
    label: 'Create Jury',
    stage: 'jury',
    quickLinks: ['/panel-pool', '/personas', '/panel-selection', '/simulations/new'],
    next: {
      label: 'Run a simulation',
      to: '/simulations/new',
      why: 'The jury exists to be argued in front of.',
    },
  },
  '/panel-pool': {
    label: 'Jury Pool',
    stage: 'jury',
    quickLinks: ['/mock-panel', '/personas', '/panel-selection', '/panel-questions', '/simulations/new'],
    next: {
      label: 'Run a simulation',
      to: '/simulations/new',
      why: 'Put this pool to work on a real argument.',
    },
  },
  '/personas': {
    label: 'Juror Personas',
    stage: 'jury',
    quickLinks: ['/panel-pool', '/mock-panel', '/panel-selection', '/panel-questions', '/simulations/new'],
    next: {
      label: 'Run a simulation',
      to: '/simulations/new',
      why: 'Persona traits only show their effect during deliberation.',
    },
  },
  '/panel-selection': {
    label: 'Jury Selection',
    stage: 'jury',
    quickLinks: ['/panel-questions', '/panel-pool', '/personas', '/panel-instructions', '/simulations/new'],
    next: {
      label: 'Draft voir dire questions',
      to: '/panel-questions',
      why: 'Selection strategy is only as good as the questions you ask.',
    },
  },
  '/panel-questions': {
    label: 'Jury Questions',
    stage: 'jury',
    quickLinks: ['/panel-selection', '/panel-instructions', '/personas', '/panel-pool', '/simulations/new'],
    next: {
      label: 'Review jury instructions',
      to: '/panel-instructions',
      why: 'The instructions frame everything the jury does with your answers.',
    },
  },
  '/panel-instructions': {
    label: 'Jury Instructions',
    stage: 'jury',
    quickLinks: ['/panel-questions', '/panel-selection', '/closing', '/rules', '/simulations/new'],
    next: {
      label: 'Test them in a simulation',
      to: '/simulations/new',
      why: 'Instructions change how a jury reads the same argument.',
    },
  },

  // ─── Arguments ─────────────────────────────────────────────────────────────
  '/generator': {
    label: 'Argument Generator',
    stage: 'arguments',
    quickLinks: ['/arguments', '/templates', '/scenarios', '/weakness', '/simulations/new'],
    next: {
      label: 'Test the draft',
      to: '/simulations/new',
      why: 'A generated argument is a hypothesis until a jury hears it.',
    },
  },
  '/scenarios': {
    label: 'Scenario Builder',
    stage: 'arguments',
    quickLinks: ['/generator', '/strategy', '/arguments', '/compare', '/simulations/new'],
    next: {
      label: 'Simulate the scenario',
      to: '/simulations/new',
      why: 'Scenarios are worth building only if you run them.',
    },
  },
  '/templates': {
    label: 'Template Library',
    stage: 'arguments',
    quickLinks: ['/arguments', '/generator', '/opening', '/closing', '/simulations/new'],
    next: {
      label: 'Run a simulation with it',
      to: '/simulations/new',
      why: 'Templates load straight into a simulation.',
    },
  },
  '/arguments': {
    label: 'Argument Repository',
    stage: 'arguments',
    quickLinks: ['/versions', '/templates', '/generator', '/weakness', '/simulations/new'],
    next: {
      label: 'Run a simulation',
      to: '/simulations/new',
      why: 'Saved arguments earn their resonance scores by being tested.',
    },
  },
  '/versions': {
    label: 'Argument Versions',
    stage: 'arguments',
    quickLinks: ['/arguments', '/compare', '/generator', '/templates', '/simulations/new'],
    next: {
      label: 'Compare the versions',
      to: '/compare',
      why: 'Versions are only meaningful next to each other.',
    },
  },
  '/opening': {
    label: 'Opening Statement',
    stage: 'arguments',
    quickLinks: ['/closing', '/templates', '/generator', '/weakness', '/simulations/new'],
    next: {
      label: 'Test the opening',
      to: '/simulations/new',
      why: 'Openings set the frame, so they are the highest-value thing to test.',
    },
  },
  '/closing': {
    label: 'Closing Argument',
    stage: 'arguments',
    quickLinks: ['/opening', '/panel-instructions', '/templates', '/weakness', '/simulations/new'],
    next: {
      label: 'Test the closing',
      to: '/simulations/new',
      why: 'Closing is the last thing the jury hears before deliberating.',
    },
  },
  '/cross-exam': {
    label: 'Cross-Exam Generator',
    stage: 'arguments',
    quickLinks: ['/witnesses', '/experts', '/depositions', '/weakness', '/simulations/new'],
    next: {
      label: 'Test the impeachment',
      to: '/simulations/new',
      why: 'See whether the jury credits the impeachment or resents it.',
    },
  },
  '/strategy': {
    label: 'Case Strategy',
    stage: 'arguments',
    quickLinks: ['/scenarios', '/weakness', '/research', '/notes', '/simulations/new'],
    next: {
      label: 'Find the weak points',
      to: '/weakness',
      why: 'Strategy holds up better once you know where it fails.',
    },
  },
  '/weakness': {
    label: 'Weakness Detector',
    stage: 'arguments',
    quickLinks: ['/arguments', '/generator', '/strategy', '/cross-exam', '/simulations/new'],
    next: {
      label: 'Re-test the revised argument',
      to: '/simulations/new',
      why: 'Confirm the fix actually raised resonance.',
    },
  },

  // ─── Simulation ────────────────────────────────────────────────────────────
  '/simulations/new': {
    label: 'Run Simulation',
    stage: 'simulate',
    quickLinks: ['/mock-panel', '/arguments', '/templates', '/history', '/compare'],
    next: {
      label: 'Review past runs',
      to: '/history',
      why: 'Earlier runs on this case set the baseline to beat.',
    },
  },
  '/simulations/:id': {
    label: 'Simulation',
    stage: 'simulate',
    quickLinks: ['/history', '/compare', '/analytics', '/mock-panel', '/simulations/new'],
    next: {
      label: 'Open simulation history',
      to: '/history',
      why: 'Every completed run is kept there with its verdict.',
    },
  },
  '/presentation': {
    label: 'Presentation Scorer',
    stage: 'simulate',
    quickLinks: ['/opening', '/closing', '/exhibits', '/feedback', '/simulations/new'],
    next: {
      label: 'Run a full simulation',
      to: '/simulations/new',
      why: 'Delivery scores pair with a jury reaction to the substance.',
    },
  },

  // ─── Analysis ──────────────────────────────────────────────────────────────
  '/simulations/:id/results': {
    label: 'Simulation Results',
    stage: 'analyze',
    quickLinks: ['/compare', '/history', '/analytics', '/weakness', '/simulations/new'],
    next: {
      label: 'Fix the weak points',
      to: '/weakness',
      why: 'Turn a low resonance score into a specific revision.',
    },
  },
  '/history': {
    label: 'Simulation History',
    stage: 'analyze',
    quickLinks: ['/compare', '/analytics', '/simulations/new', '/feedback'],
    next: {
      label: 'Compare two runs',
      to: '/compare',
      why: 'History is where you pick the pair worth comparing.',
    },
  },
  '/compare': {
    label: 'Argument Compare',
    stage: 'analyze',
    quickLinks: ['/history', '/analytics', '/versions', '/weakness', '/simulations/new'],
    next: {
      label: 'Run the next variant',
      to: '/simulations/new',
      why: 'Change one thing and measure it against this baseline.',
    },
  },
  '/feedback': {
    label: 'Post-Trial Feedback',
    stage: 'analyze',
    quickLinks: ['/analytics', '/history', '/compare', '/appeals'],
    next: {
      label: 'Check prediction accuracy',
      to: '/analytics',
      why: 'Real outcomes are what calibrate the model against your practice.',
    },
  },
  '/checklist': {
    label: 'Trial Checklist',
    stage: 'analyze',
    quickLinks: ['/tasks', '/calendar', '/rules', '/timeline'],
    next: {
      label: 'Work the task list',
      to: '/tasks',
      why: 'Checklist gaps need an owner and a due date.',
    },
  },

  // ─── Resolution ────────────────────────────────────────────────────────────
  '/settlement': {
    label: 'Settlement Calculator',
    stage: 'resolution',
    quickLinks: ['/settlement-demand', '/mediation', '/plea', '/analytics', '/simulations/new'],
    next: {
      label: 'Draft the demand letter',
      to: '/settlement-demand',
      why: 'A number is more persuasive attached to a written demand.',
    },
  },
  '/settlement-demand': {
    label: 'Settlement Demand',
    stage: 'resolution',
    quickLinks: ['/settlement', '/mediation', '/documents', '/communications'],
    next: {
      label: 'Prepare for mediation',
      to: '/mediation',
      why: 'Most demands end up on a mediation table.',
    },
  },
  '/mediation': {
    label: 'Mediation Prep',
    stage: 'resolution',
    quickLinks: ['/settlement', '/settlement-demand', '/plea', '/strategy', '/simulations/new'],
    next: {
      label: 'Check the settlement range',
      to: '/settlement',
      why: 'Walk in knowing your floor and your target.',
    },
  },
  '/plea': {
    label: 'Plea Analyzer',
    stage: 'resolution',
    quickLinks: ['/settlement', '/mediation', '/strategy', '/analytics', '/simulations/new'],
    next: {
      label: 'Test the trial alternative',
      to: '/simulations/new',
      why: 'A plea is only a good deal next to a simulated verdict.',
    },
  },
  '/appeals': {
    label: 'Appellate Tracker',
    stage: 'resolution',
    quickLinks: ['/research', '/feedback', '/calendar', '/documents'],
    next: {
      label: 'Log the trial outcome',
      to: '/feedback',
      why: 'Appellate posture starts from what actually happened at trial.',
    },
  },

  // ─── Practice ──────────────────────────────────────────────────────────────
  '/calendar': {
    label: 'Calendar',
    stage: 'practice',
    quickLinks: ['/tasks', '/checklist', '/timeline', '/rules'],
    next: {
      label: 'Review open tasks',
      to: '/tasks',
      why: 'Deadlines and tasks only work as a pair.',
    },
  },
  '/tasks': {
    label: 'Tasks',
    stage: 'practice',
    quickLinks: ['/calendar', '/checklist', '/notes', '/collaboration'],
    next: {
      label: 'Check the calendar',
      to: '/calendar',
      why: 'See which tasks are up against a filing date.',
    },
  },
  '/time': {
    label: 'Time Tracking',
    stage: 'practice',
    quickLinks: ['/invoices', '/tasks', '/firm', '/calendar'],
    next: {
      label: 'Generate an invoice',
      to: '/invoices',
      why: 'Recorded time is only revenue once it is billed.',
    },
  },
  '/invoices': {
    label: 'Invoices',
    stage: 'practice',
    quickLinks: ['/time', '/communications', '/firm', '/firm/settings'],
    next: {
      label: 'Review tracked time',
      to: '/time',
      why: 'Check nothing billable was left off.',
    },
  },
  '/firm': {
    label: 'Firm Dashboard',
    stage: 'practice',
    quickLinks: ['/firm/members', '/firm/settings', '/analytics', '/invoices'],
    next: {
      label: 'Manage seats',
      to: '/firm/members',
      why: 'Everyone who needs a login needs a seat.',
    },
  },
  '/firm/members': {
    label: 'Firm Members',
    stage: 'practice',
    quickLinks: ['/firm', '/firm/settings', '/collaboration', '/profile'],
    next: {
      label: 'Review firm settings',
      to: '/firm/settings',
      why: 'Seat changes usually mean a billing change.',
    },
  },
  '/firm/settings': {
    label: 'Firm Settings',
    stage: 'practice',
    quickLinks: ['/firm', '/firm/members', '/invoices', '/settings', '/pricing'],
    next: {
      label: 'Back to the firm dashboard',
      to: '/firm',
      why: 'Confirm the change landed the way you expected.',
    },
  },
  '/profile': {
    label: 'Profile',
    stage: 'practice',
    quickLinks: ['/settings', '/settings/notifications', '/firm', '/firm/members'],
    next: {
      label: 'Open settings',
      to: '/settings',
      why: 'Account preferences live one page over.',
    },
  },
  '/settings': {
    label: 'Settings',
    stage: 'practice',
    quickLinks: ['/settings/notifications', '/profile', '/firm/settings', '/pricing'],
    next: {
      label: 'Tune notifications',
      to: '/settings/notifications',
      why: 'Decide what Sentantia is allowed to email you about.',
    },
  },
  '/settings/notifications': {
    label: 'Notifications',
    stage: 'practice',
    quickLinks: ['/settings', '/profile', '/calendar', '/tasks'],
    next: {
      label: 'Back to settings',
      to: '/settings',
      why: 'The rest of your account preferences are there.',
    },
  },

  // ─── Public pages reachable while signed in ────────────────────────────────
  '/help': {
    label: 'Help Center',
    stage: 'practice',
    quickLinks: ['/help/walkthroughs', '/support', '/pricing', '/example-report'],
    next: {
      label: 'Follow a walkthrough',
      to: '/help/walkthroughs',
      why: 'Step-by-step guides for the parts people get stuck on.',
    },
  },
  '/help/walkthroughs': {
    label: 'Walkthroughs',
    stage: 'practice',
    quickLinks: ['/help', '/support', '/simulations/new', '/example-report'],
    next: {
      label: 'Back to the help center',
      to: '/help',
      why: 'The full reference sits behind the walkthroughs.',
    },
  },
  '/help/walkthroughs/:slug': {
    label: 'Walkthrough',
    stage: 'practice',
    quickLinks: ['/help/walkthroughs', '/help', '/simulations/new', '/example-report'],
    next: {
      label: 'Try it on a real case',
      to: '/cases',
      why: 'The walkthrough sticks once you run it against your own matter.',
    },
  },
  '/help/:sectionId': {
    label: 'Help Section',
    stage: 'practice',
    quickLinks: ['/help', '/help/walkthroughs', '/support', '/pricing'],
    next: {
      label: 'Back to the help center',
      to: '/help',
      why: 'Browse the rest of the guide.',
    },
  },
  '/support': {
    label: 'Support',
    stage: 'practice',
    quickLinks: ['/help', '/help/walkthroughs', '/pricing', '/settings'],
    next: {
      label: 'Search the help center first',
      to: '/help',
      why: 'Most questions are already answered there.',
    },
  },
  '/pricing': {
    label: 'Pricing',
    stage: 'practice',
    quickLinks: ['/firm/settings', '/firm/members', '/support', '/example-report'],
    next: {
      label: 'Review your firm plan',
      to: '/firm/settings',
      why: 'Seat count and plan are managed there.',
    },
  },
  '/example-report': {
    label: 'Example Report',
    stage: 'analyze',
    quickLinks: ['/history', '/simulations/new', '/help', '/pricing'],
    next: {
      label: 'Produce one of your own',
      to: '/simulations/new',
      why: 'A real run gives you this report for your case.',
    },
  },
  '/privacy': {
    label: 'Privacy Policy',
    stage: 'practice',
    quickLinks: ['/terms', '/support', '/help', '/settings'],
  },
  '/terms': {
    label: 'Terms of Service',
    stage: 'practice',
    quickLinks: ['/privacy', '/support', '/help', '/settings'],
  },
};

/** Route patterns with a dynamic segment, longest first so the most specific wins. */
const DYNAMIC_ROUTES: { pattern: string; regex: RegExp }[] = [
  { pattern: '/simulations/:id/results', regex: /^\/simulations\/[^/]+\/results\/?$/ },
  { pattern: '/help/walkthroughs/:slug', regex: /^\/help\/walkthroughs\/[^/]+\/?$/ },
  { pattern: '/cases/:caseId', regex: /^\/cases\/[^/]+\/?$/ },
  { pattern: '/simulations/:id', regex: /^\/simulations\/(?!new$)[^/]+\/?$/ },
  { pattern: '/help/:sectionId', regex: /^\/help\/(?!walkthroughs$)[^/]+\/?$/ },
];

/**
 * Map a live pathname onto its ROUTE_META key. Returns null when the page has
 * no entry, which is how the rail decides to stay hidden rather than guess.
 */
export function resolveRouteKey(pathname: string): string | null {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (ROUTE_META[clean]) return clean;
  const dynamic = DYNAMIC_ROUTES.find((r) => r.regex.test(clean));
  return dynamic ? dynamic.pattern : null;
}

/** Label for any path, falling back to the raw path so nothing renders blank. */
export function labelForPath(path: string): string {
  return ROUTE_META[path]?.label ?? path;
}
